import { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPatch, apiPost } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { TaskRow, relTime } from '../lib/types';
import { useAuth } from '../lib/auth';
import { Badge, Button, EmptyState, Loading } from '../components/ui';

export default function Tasks() {
  const { me } = useAuth();
  const canSeeAll = me?.canSeeAll;
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const [sweeping, setSweeping] = useState(false);
  const { data, loading, reload } = useAsync(
    () => apiGet<{ tasks: TaskRow[] }>(`/api/tasks?scope=${scope}&status=open`),
    [scope]
  );

  async function complete(id: number) {
    await apiPatch(`/api/tasks/${id}`, { status: 'done' });
    reload();
  }
  async function sweep() {
    setSweeping(true);
    try {
      await apiPost('/api/tasks/sweep', {});
      reload();
    } finally {
      setSweeping(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="h-display text-3xl text-soft">Tasks</h1>
          <p className="mt-1 text-sm text-muted">Follow-ups — including ones the tool creates when a contact goes cold.</p>
        </div>
        <div className="flex items-center gap-2">
          {canSeeAll && (
            <div className="flex rounded-lg border border-line p-0.5 text-sm">
              {(['mine', 'all'] as const).map((s) => (
                <button key={s} onClick={() => setScope(s)}
                  className={`rounded-md px-3 py-1 ${scope === s ? 'bg-power/15 text-electric' : 'text-muted'}`}>{s === 'mine' ? 'Mine' : 'Team'}</button>
              ))}
            </div>
          )}
          <Button variant="ghost" onClick={sweep} loading={sweeping}>Check for cold/stalled</Button>
        </div>
      </div>

      {loading ? (
        <Loading label="Loading tasks…" />
      ) : !data || data.tasks.length === 0 ? (
        <EmptyState title="All clear" body="No open tasks. Hit “Check for cold/stalled” to have the tool surface contacts that need a nudge." />
      ) : (
        <div className="card divide-y divide-line">
          {data.tasks.map((t) => (
            <div key={t.id} className="flex items-start gap-3 px-4 py-3">
              <button onClick={() => complete(t.id)} className="mt-1 h-5 w-5 shrink-0 rounded-md border border-line-strong transition hover:border-power" aria-label="Complete" />
              <div className="min-w-0 flex-1">
                <div className="font-head font-semibold text-soft">{t.title}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted">
                  {t.autoGenerated === 1 && <Badge className="border-victory/40 text-victory">auto</Badge>}
                  {t.contactId && <Link to={`/contacts/${t.contactId}`} className="text-electric hover:underline">{t.contactBusiness || t.contactPerson || 'contact'}</Link>}
                  {t.assigneeName && <span>· {t.assigneeName}</span>}
                  <span>· {relTime(t.dueAt)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
